// apps/mtd-itsa-web/src/components/application/toast/toast-provider.tsx

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, ShieldTick, X } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import type { ToastItem, ToastOptions, ToastVariant } from "./toast-types";

type ToastContextValue = {
    toasts: ToastItem[];
    toast: (options: ToastOptions) => { id: string; dismiss: () => void };
    dismiss: (id: string) => void;
    clear: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function iconForVariant(variant: ToastVariant) {
    switch (variant) {
        case "success":
            return ShieldTick;
        case "warning":
            return AlertTriangle;
        case "error":
            return AlertCircle;
        case "info":
        default:
            return AlertCircle;
    }
}

function stylesForVariant(variant: ToastVariant) {
    switch (variant) {
        case "success":
            return {
                iconWrap: "bg-success-primary10 text-success-primary",
                ring: "ring-secondary",
            };
        case "warning":
            return {
                iconWrap: "bg-warning-primary10 text-warning-primary",
                ring: "ring-secondary",
            };
        case "error":
            return {
                iconWrap: "bg-error-primary10 text-error",
                ring: "ring-secondary",
            };
        case "info":
        default:
            return {
                iconWrap: "bg-secondary text-fg-secondary",
                ring: "ring-secondary",
            };
    }
}

function ToastCard({
    item,
    onDismiss,
}: {
    item: ToastItem;
    onDismiss: () => void;
}) {
    const Icon = iconForVariant(item.variant);
    const s = stylesForVariant(item.variant);

    // State to track remaining time and pause status
    const [remainingMs, setRemainingMs] = useState(item.durationMs);
    const [isPaused, setIsPaused] = useState(false);

    // Refs to manage timer logic without causing re-renders
    const timerRef = useRef<number | null>(null);
    const startedAtRef = useRef<number | null>(null);
    const onDismissRef = useRef(onDismiss);

    // Keep onDismissRef fresh so we don't need to add onDismiss to the effect dependencies
    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    const shouldAutoDismiss = item.durationMs > 0;

    const pause = useCallback(() => {
        if (!shouldAutoDismiss || isPaused || remainingMs <= 0) return;

        // Clear the active timer
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Calculate how much time passed since the timer started
        if (startedAtRef.current) {
            const elapsed = Date.now() - startedAtRef.current;
            setRemainingMs((prev) => Math.max(0, prev - elapsed));
        }

        setIsPaused(true);
    }, [shouldAutoDismiss, isPaused, remainingMs]);

    const resume = useCallback(() => {
        if (!shouldAutoDismiss || !isPaused || remainingMs <= 0) return;
        setIsPaused(false);
    }, [shouldAutoDismiss, isPaused, remainingMs]);

    // Main Timer Effect
    useEffect(() => {
        // Don't start timer if:
        // 1. Auto-dismiss is disabled
        // 2. Currently paused
        if (!shouldAutoDismiss || isPaused) return;

        // If no time remaining, dismiss immediately
        if (remainingMs <= 0) {
            onDismissRef.current();
            return;
        }

        // Start the timer
        startedAtRef.current = Date.now();
        timerRef.current = window.setTimeout(() => {
            onDismissRef.current();
        }, remainingMs);

        // Cleanup: clear timeout on unmount or before next effect run
        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
            }
        };
    }, [remainingMs, isPaused, shouldAutoDismiss]);

    return (
        <div
            role={item.variant === "error" ? "alert" : "status"}
            className={cx(
                "pointer-events-auto relative flex gap-3 rounded-xl bg-primary p-4 shadow-lg ring-1",
                s.ring,
                // lightweight enter animation
                "animate-in fade-in slide-in-from-right-2 duration-200"
            )}
            onMouseEnter={pause}
            onMouseLeave={resume}
            onFocusCapture={pause}
            onBlurCapture={resume}
        >
            <div
                className={cx(
                    "flex size-10 shrink-0 items-center justify-center rounded-full",
                    s.iconWrap
                )}
            >
                <Icon className="size-5" />
            </div>

            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-primary">{item.title}</div>
                {item.description ? (
                    <div className="mt-0.5 text-sm text-tertiary">{item.description}</div>
                ) : null}

                {item.action ? (
                    <div className="mt-3">
                        <button
                            type="button"
                            className="inline-flex items-center rounded-lg border border-secondary bg-primary px-3 py-1.5 text-sm font-medium text-primary shadow-xs hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2"
                            onClick={() => {
                                item.action?.onPress();
                                onDismiss();
                            }}
                        >
                            {item.action.label}
                        </button>
                    </div>
                ) : null}
            </div>

            <button
                type="button"
                aria-label="Dismiss notification"
                className="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-lg text-fg-quaternary hover:bg-secondary hover:text-fg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2"
                onClick={onDismiss}
            >
                <X className="size-4" />
            </button>
        </div>
    );
}

function ToastViewport({
    toasts,
    dismiss,
}: {
    toasts: ToastItem[];
    dismiss: (id: string) => void;
}) {
    if (typeof document === "undefined") return null;

    return createPortal(
        <div className="fixed bottom-0 right-0 z-50 flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]">
            {toasts.map((t) => (
                <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
            ))}
        </div>,
        document.body
    );
}

export function ToastProvider({
    children,
    maxToasts = 4,
    defaultDurationMs = 5000,
}: {
    children: ReactNode;
    maxToasts?: number;
    defaultDurationMs?: number;
}) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const dismiss = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const clear = useCallback(() => {
        setToasts([]);
    }, []);

    const toast = useCallback(
        (options: ToastOptions) => {
            const id =
                crypto?.randomUUID?.() ??
                `${Date.now()}-${Math.random().toString(16).slice(2)}`;

            const item: ToastItem = {
                id,
                title: options.title,
                description: options.description,
                variant: options.variant ?? "info",
                durationMs: options.durationMs ?? defaultDurationMs,
                action: options.action,
                createdAt: Date.now(),
            };

            setToasts((prev) => [item, ...prev].slice(0, maxToasts));

            return { id, dismiss: () => dismiss(id) };
        },
        [defaultDurationMs, dismiss, maxToasts]
    );

    const value = useMemo<ToastContextValue>(
        () => ({
            toasts,
            toast,
            dismiss,
            clear,
        }),
        [clear, dismiss, toast, toasts]
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastViewport toasts={toasts} dismiss={dismiss} />
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within a ToastProvider");
    return ctx;
}
